import type { ITransport, ITransportResponse } from "@cubejs-client/core";
import { HttpTransport } from "@cubejs-client/core";
import { showWarningMessage } from "@/features/alertSnackbarSlice/alertSnackbarSlice";
import { isCubeError } from "@/helpers/cube.helpers";
import logger from "@/helpers/logger";
import { store } from "@/store";
import {
  ERROR_WINDOW_MINS,
  ERROR_WINDOW_MS,
  track500Error,
} from "./cube500Tracker";

/**
 * Custom transport that wraps HttpTransport to monitor HTTP 500 errors.
 *
 * Intercepts all Cube API responses and tracks 500 errors. When a threshold
 * of errors is exceeded within a time window, displays a warning to the user.
 *
 * All responses and errors are passed through transparently to preserve
 * existing behavior.
 */
export class MonitoredTransport implements ITransport<Response> {
  private transport: HttpTransport;

  constructor(transport: HttpTransport) {
    this.transport = transport;
  }

  get authorization() {
    return this.transport.authorization;
  }

  set authorization(value: string | undefined) {
    this.transport.authorization = value;
  }

  request(
    method: string,
    params: Record<string, unknown>
  ): ITransportResponse<Response> {
    const originalResponse = this.transport.request(method, params);

    return {
      subscribe: async (callback) => {
        return originalResponse.subscribe((result, resubscribe) => {
          // Check if this is an error response with status 500
          if (this.is500Error(result)) {
            logger.warn(
              "[MonitoredTransport] Detected 500 error from Cube API"
            );

            // Track the error and check if we should show a warning
            const shouldWarn = track500Error();

            if (shouldWarn) {
              this.dispatchWarning();
            }
          }

          // Pass through the result to the original callback
          return callback(result, resubscribe);
        });
      },
      unsubscribe: originalResponse.unsubscribe,
    };
  }

  /**
   * Check if a response represents a 500 error.
   * Handles both error objects with status property and Response objects.
   */
  private is500Error(result: Response | { error: string }): boolean {
    // Check if it's a CubeError object with status
    if (isCubeError(result) && result.status === 500) {
      return true;
    }

    // Check if it's a Response object (from fetch)
    if (result instanceof Response && result.status === 500) {
      return true;
    }

    // Check if it's an error object with status property
    if (
      typeof result === "object" &&
      result !== null &&
      "status" in result &&
      (result as { status: number }).status === 500
    ) {
      return true;
    }

    return false;
  }

  /**
   * Dispatch a warning notification to the user about service issues.
   */
  private dispatchWarning() {
    store.dispatch(
      showWarningMessage({
        duration: ERROR_WINDOW_MS,
        message: `The data service is restarting - please try again in ${ERROR_WINDOW_MINS} minutes.
        Contact the Arenko Help Desk if the problem persists.`,
      })
    );
  }
}
